import { useMemo, useState } from "react";
import { Pressable, Text, TextInput, View } from "react-native";
import { useTranslation } from "react-i18next";
import { buildDayPanel } from "@jackedlogs/shared";
import { resolveTheme } from "../theme";
import { useThemePreferenceContext } from "../ThemeContext";

import { makeEditorStyles } from "../ProgramEditor/styles";
import { useBreakpoint } from "../useBreakpoint";
import { makeComposerStyles } from "./composerStyles";
import { makePlanStyles } from "./planComposerStyles";
import { useComposer } from "./useComposer";
import { useEditInPlace } from "./useEditInPlace";
import { useKindLabels } from "./useKindLabels";
import { ComposerEditLayout } from "./ComposerEditLayout";
import { PlanExerciseEditor } from "./PlanExerciseEditor";
import type { ProgramComposerProps } from "./types";

// Single-mode (Plan) editor — the "edit-where-the-exercise-sits" rework
// (next/plans-editor-usability.md). It replaces the program composer's
// compose-form-on-top (which forced select-at-bottom → edit-at-top scroll
// churn) with the shared edit-in-place shell (ComposerEditLayout +
// useEditInPlace): narrow = accordion, wide = master-detail. cycleWeeks is
// pinned to 1 → every entry is one block on day 0, week 0;
// buildDayPanel(blocks, 0) is the exercise list. This file is now just the
// Plan-specific meta band + the per-exercise editor (PlanExerciseEditor) +
// the picker; the layout, the list rows, and the live staging→commit state
// machine are the same pieces the program composer uses.
export function PlanComposer({
  durableWrite,
  dark,
  plan,
  catalog,
  Picker,
  DateField,
  onSaved,
  onCancel,
  unitsPreference,
}: ProgramComposerProps) {
  const { t } = useTranslation();
  const { wide } = useBreakpoint();
  const family = useThemePreferenceContext()?.preference.family ?? "brand";
  const theme = resolveTheme(dark, family);
  const p = theme.palette;
  const cs = useMemo(() => makeComposerStyles(p), [p]);
  const ps = useMemo(() => makePlanStyles(p), [p]);
  const selectStyles = useMemo(() => makeEditorStyles(p), [p]);

  const composer = useComposer({
    durableWrite,
    catalog,
    onSaved,
    mode: "single",
    plan,
    unitsPreference,
  });
  const { draft, setDraft, busy, error, blocks, form, formConflict, cellUnit } =
    composer;
  const kindLabels = useKindLabels(cellUnit);
  const { scheduledFor, setScheduledFor } = composer;

  const entries = useMemo(
    () => buildDayPanel(blocks, 0, kindLabels),
    [blocks, kindLabels],
  );
  const resolveName = useMemo(() => {
    const bySlug = new Map(catalog.map((c) => [c.slug, c.name]));
    return (id: string, snapshot?: string) => snapshot ?? bySlug.get(id) ?? id;
  }, [catalog]);

  const ip = useEditInPlace({ composer, entries });
  const [detailsOpen, setDetailsOpen] = useState(
    () => scheduledFor !== "" || draft.description !== "",
  );

  // Exclude EVERY exercise already in the plan (committed entries + the open
  // editor's members) from the picker. A single-workout plan never wants the
  // same exercise twice, and — since the picker is the only add/swap path —
  // this makes a real slug conflict at commit() unreachable.
  const presentSlugs = useMemo(() => {
    const s = new Set(form.members.map((m) => m.exerciseId));
    for (const e of entries) {
      if (e.kind === "composed") for (const m of e.members) s.add(m.exerciseId);
      else s.add(e.exerciseId);
    }
    return [...s];
  }, [form.members, entries]);

  const metaBand = (
    <View style={ps.metaBand}>
      <Text style={cs.fieldLabel}>{t("plans.name", { defaultValue: "Name" })}</Text>
      <TextInput
        style={cs.input}
        value={draft.name}
        onChangeText={(v) => setDraft((d) => ({ ...d, name: v }))}
        placeholder={t("plans.namePlaceholder", { defaultValue: "e.g. Squat day" })}
        placeholderTextColor={p.ink3}
        accessibilityLabel={t("plans.name", { defaultValue: "Name" })}
      />
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: detailsOpen }}
        onPress={() => setDetailsOpen((o) => !o)}
        style={ps.disclosureBtn}
      >
        <Text style={ps.disclosureText}>
          {detailsOpen ? "▾" : "▸"} {t("plans.details", { defaultValue: "Details" })}
        </Text>
      </Pressable>
      {detailsOpen ? (
        <>
          <Text style={cs.fieldLabel}>
            {t("plans.scheduledFor", { defaultValue: "Scheduled for (optional)" })}
          </Text>
          {DateField ? (
            <DateField value={scheduledFor} onChange={setScheduledFor} />
          ) : (
            <TextInput
              style={cs.input}
              value={scheduledFor}
              onChangeText={setScheduledFor}
              placeholder="YYYY-MM-DD"
              placeholderTextColor={p.ink3}
              autoCapitalize="none"
              accessibilityLabel={t("plans.scheduledFor", {
                defaultValue: "Scheduled for (optional)",
              })}
            />
          )}
          <Text style={cs.fieldLabel}>
            {t("plans.notes", { defaultValue: "Notes (optional)" })}
          </Text>
          <TextInput
            style={[cs.input, cs.inputMultiline]}
            value={draft.description}
            onChangeText={(v) => setDraft((d) => ({ ...d, description: v }))}
            multiline
            placeholderTextColor={p.ink3}
            accessibilityLabel={t("plans.notes", { defaultValue: "Notes (optional)" })}
          />
        </>
      ) : null}
    </View>
  );

  const editor = (
    <>
      {formConflict ? (
        <Text style={cs.errorText}>
          {t("composer.dupExercise", {
            name: resolveName(formConflict.slug),
            day: formConflict.dayIdx + 1,
            defaultValue: "{{name}} is already on D{{day}} in the selected weeks",
          })}
        </Text>
      ) : null}
      <PlanExerciseEditor
        composer={composer}
        resolveName={resolveName}
        cs={cs}
        ps={ps}
        selectStyles={selectStyles}
        theme={theme}
        onCollapse={ip.collapse}
        onRemove={ip.removeCurrent}
        onReplaceMember={(idx) => ip.setPicker({ replace: idx })}
      />
    </>
  );

  const picker = (
    <Picker
      visible={ip.picker !== null}
      onClose={() => ip.setPicker(null)}
      onAddMany={ip.onAddMany}
      onCreateSuperset={({ addSlugs }) => ip.onCreateSuperset(addSlugs)}
      presentSlugs={presentSlugs}
      // LG-41 — the ONE surface still hiding, and not by preference: a
      // PlannedExerciseEntry carries no id (plan.ts), so a second entry with
      // the same slug is ambiguous to everything resolving by slug (LG-40).
      presentTreatment="hide"
    />
  );

  return (
    <ComposerEditLayout
      cs={cs}
      ps={ps}
      theme={theme}
      wide={wide}
      busy={busy}
      backLabel={t("plans.title", { defaultValue: "Plans" })}
      title={
        draft.name ||
        (plan
          ? t("plans.editingTitle", { defaultValue: "Edit plan" })
          : t("plans.creatingTitle", { defaultValue: "New plan" }))
      }
      onCancel={onCancel}
      onSave={ip.handleSave}
      error={error}
      metaBand={metaBand}
      sectionLabel={t("plans.exercisesLabel", { defaultValue: "Exercises" })}
      emptyHint={t("plans.editorEmpty", {
        defaultValue: "No exercises yet. Add one to start building this plan.",
      })}
      selectPrompt={t("plans.selectExercisePrompt", {
        defaultValue: "Select an exercise to edit its sets.",
      })}
      entries={entries}
      resolveName={resolveName}
      editingIdx={ip.editingIdx}
      editing={ip.editing}
      composingNew={ip.composingNew}
      hasForm={ip.hasForm}
      editor={editor}
      onOpenEntry={ip.openEntry}
      onOpenAdd={ip.addExercise}
      addLabel={t("plans.addExerciseCta", { defaultValue: "Add exercise" })}
      picker={picker}
    />
  );
}
